import React, { useMemo } from 'react';
import {
  isMetaAnswerKey,
  isPngFileName,
  normalizeFileAnswerValueForKey,
  prettyAnswerKey,
  uploadedFilesFromAnswers,
} from '../../../features/flow/utils';

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function isDateLikeKey(key: string) {
  return /(date|dob|birth|year)/i.test(key);
}

function isIsoDateString(v: unknown) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
}

function isDeductionsReviewKey(key: string): boolean {
  // Keep this scoped to deductions-related flows.
  return (
    /^child_/.test(key) ||
    /^nursery_/.test(key) ||
    /^education_/.test(key) ||
    /^care_/.test(key) ||
    /^care_provider_\d+_/.test(key) ||
    /^charitable_/.test(key) ||
    /^regional_/.test(key) ||
    /^medical_/.test(key) ||
    /^student_loan_/.test(key) ||
    /^mortgage_/.test(key) ||
    /^hsa_/.test(key) ||
    key === 'health_savings_account' ||
    /^work_related_/.test(key) ||
    /^ira_/.test(key) ||
    /^self_employed_health_/.test(key) ||
    /^paid_tax_abroad/.test(key) ||
    /^investment_interest_/.test(key) ||
    /^electric_hybrid_/.test(key) ||
    /^vehicle_/.test(key) ||
    /^seller_/.test(key) ||
    /^main_residence_/.test(key) ||
    /deduction/i.test(key)
  );
}

function fileGroupLabel(answerKey: string): string {
  const provider = answerKey.match(/^care_provider_(\d+)_(.+)$/);
  if (provider) return `Care Provider ${provider[1]} ${prettyAnswerKey(provider[2])}`;
  return prettyAnswerKey(answerKey);
}

type Row = { key: string; label: string };

export function DeductionsReviewScreenBody({
  answers,
  actionBusy,
  setAnswers,
}: {
  answers: Record<string, unknown>;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const sections = useMemo(() => {
    const keys = Object.keys(answers ?? {}).filter((k) => !isMetaAnswerKey(k) && isDeductionsReviewKey(k));

    const push = (arr: Row[], key: string) => {
      const v = answers[key];
      if (isEmptyValue(v)) return;
      arr.push({ key, label: prettyAnswerKey(key) });
    };
    const section = (title: string, rows: Row[]) => ({ title, rows });

    const childDependentCare: Row[] = [];
    keys
      .filter((k) => /^child_/.test(k) || /^nursery_/.test(k) || /^education_/.test(k) || /^care_/.test(k) || /^care_provider_\d+_/.test(k))
      .sort()
      .forEach((k) => push(childDependentCare, k));

    const donations: Row[] = [];
    keys
      .filter((k) => /^charitable_/.test(k) || /^regional_/.test(k))
      .sort()
      .forEach((k) => push(donations, k));

    const health: Row[] = [];
    keys
      .filter((k) => /^medical_/.test(k) || /^hsa_/.test(k) || k === 'health_savings_account')
      .sort()
      .forEach((k) => push(health, k));

    const loansRetirement: Row[] = [];
    keys
      .filter((k) => /^student_loan_/.test(k) || /^ira_/.test(k) || /^self_employed_health_/.test(k) || /^investment_interest_/.test(k))
      .sort()
      .forEach((k) => push(loansRetirement, k));

    const housingTransport: Row[] = [];
    keys
      .filter((k) => /^mortgage_/.test(k) || /^main_residence_/.test(k) || /^electric_hybrid_/.test(k) || /^vehicle_/.test(k) || /^seller_/.test(k))
      .sort()
      .forEach((k) => push(housingTransport, k));

    const other: Row[] = [];
    keys
      .filter(
        (k) =>
          !(
            /^child_/.test(k) ||
            /^nursery_/.test(k) ||
            /^education_/.test(k) ||
            /^care_/.test(k) ||
            /^care_provider_\d+_/.test(k) ||
            /^charitable_/.test(k) ||
            /^regional_/.test(k) ||
            /^medical_/.test(k) ||
            /^hsa_/.test(k) ||
            k === 'health_savings_account' ||
            /^student_loan_/.test(k) ||
            /^ira_/.test(k) ||
            /^self_employed_health_/.test(k) ||
            /^investment_interest_/.test(k) ||
            /^mortgage_/.test(k) ||
            /^main_residence_/.test(k) ||
            /^electric_hybrid_/.test(k) ||
            /^vehicle_/.test(k) ||
            /^seller_/.test(k)
          )
      )
      .sort()
      .forEach((k) => push(other, k));

    return [
      section('Child & Dependent Care', childDependentCare),
      section('Donations', donations),
      section('Health / HSA', health),
      section('Loans / Retirement', loansRetirement),
      section('Housing / Transport', housingTransport),
      section('Other Deductions', other),
    ].filter((s) => s.rows.length > 0);
  }, [answers]);

  const uploadedFilesForReview = useMemo(
    () =>
      uploadedFilesFromAnswers(answers)
        .filter((row) => isDeductionsReviewKey(row.answerKey))
        .map((row) => ({
          ...row,
          groupLabel: fileGroupLabel(row.answerKey),
        }))
        .sort((a, b) => {
          const s = a.groupLabel.localeCompare(b.groupLabel);
          if (s !== 0) return s;
          const k = a.answerKey.localeCompare(b.answerKey);
          if (k !== 0) return k;
          return a.fileIndex - b.fileIndex;
        }),
    [answers]
  );

  return (
    <div className="w-full flex flex-col gap-10">
      {sections.map((sec, idx) => (
        <div key={sec.title} className="w-full">
          {idx > 0 ? <div className="w-full border-t border-[#E2E8F0] pt-10" /> : null}
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-base font-medium text-[#131315]">{sec.title}:</div>
          </div>

          <div className="mt-6 w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
            {sec.rows.map(({ label, key }) => {
              const v = answers[key];
              const valStr = v === null || v === undefined ? '' : String(v);
              const isNum = typeof v === 'number';
              const isArr = Array.isArray(v);
              const isDate = isIsoDateString(v) || isDateLikeKey(key);

              return (
                <div key={key} className="min-w-0 col-span-1">
                  <div className="flex flex-col gap-3">
                    <label className="block text-sm text-[#131315] font-medium">{label}</label>
                    {isArr ? (
                      <textarea
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        value={JSON.stringify(v)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          try {
                            const parsed = JSON.parse(raw);
                            setAnswers((prev) => ({ ...prev, [key]: parsed }));
                          } catch {
                            setAnswers((prev) => ({ ...prev, [key]: raw }));
                          }
                        }}
                        rows={3}
                        disabled={actionBusy}
                      />
                    ) : (
                      <input
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        type={isDate ? 'date' : isNum ? 'number' : 'text'}
                        value={valStr}
                        onChange={(e) =>
                          setAnswers((prev) => ({
                            ...prev,
                            [key]: isNum ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          }))
                        }
                        disabled={actionBusy}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {uploadedFilesForReview.length > 0 ? (
        <div className="w-full border-t border-[#E2E8F0] pt-10">
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-sm font-semibold text-[#131315]">Uploaded Files:</div>
          </div>

          <div className="mt-4 w-full max-w-[668px] flex flex-col gap-3">
            {(() => {
              const seen = new Map<string, number>();
              return uploadedFilesForReview.map((row) => {
                const next = (seen.get(row.groupLabel) ?? 0) + 1;
                seen.set(row.groupLabel, next);
                return (
                  <div key={`${row.answerKey}_${row.fileName}_${row.fileIndex}`}>
                    <div className="text-xs text-[#525353] mb-2">{row.groupLabel}</div>
                    <div className="w-full max-w-[310px] rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] px-4 py-3 flex items-center justify-between">
                      <span className="text-sm text-[#131315] break-all">
                        <span className="block text-base font-medium text-[#131315] mb-1">
                          File {next}: {row.fileName}
                        </span>
                      </span>
                      <button
                        type="button"
                        disabled={actionBusy}
                        onClick={() => {
                          setAnswers((prev) => {
                            const curVal = prev[row.answerKey];
                            const curAll = normalizeFileAnswerValueForKey(row.answerKey, curVal);
                            let pngSeen = -1;
                            const nextAll = curAll.filter((ref) => {
                              if (!isPngFileName(ref.name)) return true;
                              pngSeen += 1;
                              if (pngSeen === row.fileIndex && ref.name === row.fileName) return false;
                              return true;
                            });
                            return { ...prev, [row.answerKey]: nextAll.length > 0 ? nextAll : null };
                          });
                        }}
                        className="text-base font-medium text-[#131315] disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
